import Crypto from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';

/**
 * A singleton class which can be used to encrypt a localstorage entry. In this application it is mainly used
 * with vuex store. These functions working with the complete store at once, not with
 * values inside the store.
 * The class functions getItem, setItem and removeItem are oriented at localStorage functions.
 */
export default class EncryptionStorage {

    /**
     * Singleton instance
     */
    private static instance: EncryptionStorage;

    /**
     * Singleton factory
     */
    public static getInstance(): EncryptionStorage {
        if (!this.instance) {
            this.instance = new EncryptionStorage();
        }
        return this.instance;
    }

    /**
     * Seed for hashing the store data
     */
    private seed!: string;

    private constructor() {
        // remove old store value
        // ATTENTION: Can be removed in future
        localStorage.removeItem('SWR');
        // looking for saved seed
        const dynamicSeedPart = localStorage.getItem(process.env.VUE_APP_STORAGE_SEED_KEY!);
        // create new one if not available
        if (!dynamicSeedPart) {
            this.refreshSeed();
        } else {
            // merge dynamic and static part
            this.seed = `${dynamicSeedPart}${process.env.VUE_APP_STORAGE_SEED_STATIC}`;
        }
        this.checkEncryption();
    }

    /**
     * Checks if an encryption was made to the store entry already.
     * If not, encrypt the entry. If so, do nothing.
     * This function is mainly required for backwards compatibility.
     */
    private checkEncryption() {
        const currentStoreRaw = localStorage.getItem(process.env.VUE_APP_STORAGE_KEY!);
        if (currentStoreRaw) {
            try {
                // this parsing is necessary to check if encryption was done before
                JSON.parse(currentStoreRaw);
                // store is not encrypted
                const encryptedStore = Crypto.AES.encrypt(currentStoreRaw, this.seed!).toString();
                // Save the encrypted store in local storage.
                localStorage.setItem(process.env.VUE_APP_STORAGE_KEY!, encryptedStore);
            } catch (e) {
                // -- everything is fine, store is encrypted
                // I know, using exceptions for code flow is a bad practice...
                // but it is not possible to solve it better
            }
        }
    };

    /**
     * Returns the store entry
     */
    public getItem(): any | null {
        // Get the store from local storage.
        const store = localStorage.getItem(process.env.VUE_APP_STORAGE_KEY!);
        if (store) {
            try {
                // Decrypt the store retrieved from local storage
                // using our encryption token stored in cookies.
                const bytes = Crypto.AES.decrypt(store, this.seed);
                return JSON.parse(bytes.toString(Crypto.enc.Utf8));
            } catch (e) {
                // The store will be reset if decryption fails.
                localStorage.removeItem(process.env.VUE_APP_STORAGE_KEY!);
            }
        }
        return null;
    };

    /**
     * Stores the stores encrypted
     * @param key
     * @param data
     */
    public setItem(key: string, data: string): void {
        // Encrypt the store using our encryption token stored in cookies.
        this.refreshSeed();
        const store = Crypto.AES.encrypt(data, this.seed).toString();
        // Save the encrypted store in local storage.
        localStorage.setItem(process.env.VUE_APP_STORAGE_KEY!, store);
    };

    public removeItem() {
        localStorage.removeItem(process.env.VUE_APP_STORAGE_KEY!);
    };

    /**
     * Creates a new seed and saves it
     */
    private refreshSeed() {
        const dynamicSeedPart = Crypto.AES.encrypt(uuidv4(), process.env.VUE_APP_STORAGE_SEED_STATIC!).toString();
        localStorage.setItem(process.env.VUE_APP_STORAGE_SEED_KEY!, dynamicSeedPart);
        // merge dynamic and static part
        this.seed = `${dynamicSeedPart}${process.env.VUE_APP_STORAGE_SEED_STATIC}`;
    }
};
